import {inject, Injectable, signal} from '@angular/core';
import {HttpErrorResponse} from '@angular/common/http';
import {MessageService} from 'primeng/api';
import {EmailsClient} from '../api-client';
import {describeHttpError, silentErrors} from '../shared/http-context';

/**
 * Downloads one email's raw .eml, from wherever an email is listed (the Emails tab, a user's page).
 *
 * The API hands back a short-lived pre-signed S3 URL rather than the bytes, so the mail never
 * passes through the browser's XHR at all -- this only asks for the URL and lets the browser fetch
 * it. Every call is audited and counts against a daily limit server-side, which is why it is one
 * shared service: an operator's budget should not depend on which screen they clicked from.
 */
@Injectable({providedIn: 'root'})
export class EmailDownloadService {
  private readonly emailsClient = inject(EmailsClient);
  private readonly messageService = inject(MessageService);

  /** The email currently being fetched, so a row can show a spinner on its own button. */
  private readonly downloadingId = signal<string | undefined>(undefined);
  public readonly downloadingEmailId = this.downloadingId.asReadonly();

  public download(emailId: string): void {
    if (this.downloadingId() !== undefined) {
      return;
    }

    this.downloadingId.set(emailId);
    this.emailsClient
      // silentErrors: hitting the daily download limit is the one failure an operator needs to
      // read carefully, and the interceptor's "Request failed - 429 Too Many Requests: ..." buries
      // the API's explanation of when the quota frees up behind the status line.
      .downloadEmail(emailId, 'body', false, {context: silentErrors()})
      .subscribe({
        next: ({url}) => {
          this.downloadingId.set(undefined);
          this.startDownload(url);
        },
        error: (error: unknown) => {
          this.downloadingId.set(undefined);
          this.reportFailure(error);
        },
      });
  }

  /**
   * A plain same-tab navigation rather than `window.open`: the URL only exists after the request
   * comes back, and by then the click is long over, so a popup blocker eats a new window. The
   * pre-signed URL carries `Content-Disposition: attachment`, so the browser saves the file and
   * leaves the page where it is instead of navigating to it.
   */
  private startDownload(url: string): void {
    window.location.assign(url);
  }

  private reportFailure(error: unknown): void {
    if (error instanceof HttpErrorResponse && error.status === 429) {
      this.messageService.add({
        severity: 'warn',
        summary: 'Download limit reached',
        detail: describeHttpError(error),
        life: 12000,
      });
      return;
    }

    this.messageService.add({
      severity: 'error',
      summary: 'Could not download email',
      detail: error instanceof HttpErrorResponse ? describeHttpError(error) : 'Unexpected error',
    });
  }
}
